import { gql, useQuery } from "@apollo/client";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlexDiv, SkeletonBlock } from "src/Components/UI";
import { PhoenixIcon, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { chevron_left, chevron_right } from "src/images/NewDesign";
import { metricLabelMap } from "src/utils/misc";
import { theme } from "src/utils/theme";
import styled from "styled-components";
import { KPITile } from "./KPITile";
import { KPIWindow } from "./KPIWindow";

const FETCH_USER_STACK_RANK = gql`
  query getStackRankMetrics($upperbound_date: DateTime, $lowerbound_date: DateTime) {
    getStackRankMetrics(upperbound_date: $upperbound_date, lowerbound_date: $lowerbound_date) {
      metric
      list_value
      list_rank_of
      improvement_status
    }
  }
`;

const valueTypeMap: Record<string, string> = {
  DMConnectRate: "%",
  DMSetRate: "%",
  DMTotalSets: "",
  AllHolds: "",
  HoldRate: "%",
  Logos: "",
  CloseRate: "%",
  TotalRevenueValue: "$",
  WinPerRepPerDay: "",
  Dials: "",
  ColdCalls: "",
  Connects: "",
  NDMColdCalls: "",
  DMColdCalls: "",
  InitialSets: "",
  RescheduledSets: "",
  AllSets: "",
  Pipeline: "",
  OTFS: "",
  InitialHolds: "",
  RescheduledHolds: "",
  FollowUpHolds: "",
  AllSales: "",
  TotalRecurringRevenue: "$",
  TotalRevenue: "$",
};

const timeframeOptions = [
  { label: "Today", value: [moment().startOf("day"), moment().endOf("day")] },
  { label: "Yesterday", value: [moment().subtract(1, "day").startOf("day"), moment().subtract(1, "day").endOf("day")] },
  { label: "This Week", value: [moment().startOf("week"), moment().endOf("week")] },
  {
    label: "Last Week",
    value: [moment().subtract(1, "week").startOf("week"), moment().subtract(1, "week").endOf("week")],
  },
  { label: "This Month", value: [moment().startOf("month"), moment().endOf("month")] },
  {
    label: "Last Month",
    value: [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
  },
  { label: "This Year", value: [moment().startOf("year"), moment().endOf("year")] },
];

export const RepKPIList: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [maxWidth, setMaxWidth] = useState(840);

  const [showKPIWindow, setShowKPIWindow] = useState(false);
  const [timeframe, setTimeframe] = useState(timeframeOptions[0]);

  const selectedStat = useRef<string | null>(null);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: "free-snap",
    renderMode: "performance",
    slides: {
      perView: "auto",
      spacing: 8,
    },
  });

  const handleScrollForward = useCallback(() => {
    if (!sliderRef) return;
    slider.current?.next();
  }, [sliderRef]);
  const handleScrollBack = useCallback(() => {
    if (!sliderRef) return;
    slider.current?.prev();
  }, [sliderRef]);

  const { data, loading } = useQuery(FETCH_USER_STACK_RANK, {
    fetchPolicy: "cache-and-network",
    variables: {
      lowerbound_date: moment.utc(timeframe.value[0]).startOf("day").format(),
      upperbound_date: moment.utc(timeframe.value[1]).endOf("day").format(),
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });

  const handleKPIClose = useCallback(() => {
    setShowKPIWindow(false);
  }, [setShowKPIWindow]);

  const formattedData = useMemo(() => {
    const rawData = data?.getStackRankMetrics;
    if (!rawData) return [];

    const desiredOrder = Object.keys(metricLabelMap);

    const sortedData = [...rawData].sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.metric);
      const indexB = desiredOrder.indexOf(b.metric);
      return indexA - indexB;
    });

    return sortedData.map((item: any) => {
      const { metric, list_value, list_rank_of, improvement_status } = item;
      const label = metricLabelMap[metric];
      const formattedValue = `${list_value}${valueTypeMap[metric]}`;
      return {
        metric,
        label,
        value: formattedValue,
        list_rank_of,
        improvement_status,
      };
    });
  }, [data]);

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newMaxWidth = window.innerWidth - rect.left;
      setMaxWidth(newMaxWidth);
    };
    setTimeout(() => {
      handleResize();
    }, 750);
    data && handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data]);

  const handleShowWindow = useCallback((metric: string) => {
    selectedStat.current = metric;
    setShowKPIWindow(true);
  }, []);

  return (
    <Container>
      {showKPIWindow && (
        <KPIWindow
          handleClose={handleKPIClose}
          formattedData={formattedData}
          initialStat={selectedStat.current || ""}
          timeframe={timeframe}
        />
      )}

      <PhoenixMultiSelect
        isMulti={false}
        name="time_frame"
        options={timeframeOptions}
        placeholder="Time Frame"
        onChange={(e: { label: string; value: any[] }) => setTimeframe(e)}
        value={timeframe}
        isClearable={false}
        marginBottom={false}
        error={false}
        ghostSelect
        ghostSelectAppThemeOverride="Light"
        width={95}
        optionsContainerWidth={120}
        style={{ overflow: "hidden" }}
        lineHeight="10px"
        pointer
        menuInset="40px 0px 0px -1px"
        menuBorder
        removeMenuPadding
      />

      <KPIList align="center" maxWidth={maxWidth} ref={containerRef}>
        {maxWidth !== 0 && !loading && (
          <div ref={sliderRef} className="keen-slider">
            {formattedData.map((item: any, i: number) => (
              <KPITile key={item.label} index={i} data={item} handleShowWindow={handleShowWindow} />
            ))}
          </div>
        )}
        {loading && (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <SkeletonBlock
                key={`loadblock-${index}`}
                height="40px"
                width="140px"
                borderRadius={6}
                opacity={1 - index * 0.19}
                delayNumber={index}
                animationDuration={3}
                style={{ marginRight: "8px" }}
              />
            ))}
          </>
        )}
      </KPIList>

      <ScrollArrows handleScrollForward={handleScrollForward} handleScrollBack={handleScrollBack} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  height: 40px;

  cursor: grab;
  :active {
    cursor: grabbing;
  }
`;

const KPIList = styled(FlexDiv)<{ maxWidth: number }>`
  position: relative;

  max-width: ${(props) => props.maxWidth}px;
  width: 100%;

  overflow-x: hidden;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    content: "";

    width: 40px;
    height: 100%;

    pointer-events: none;
    background: linear-gradient(to left, ${theme.fill.neutral.tertiary}, rgba(245, 245, 245, 0));
  }
`;

interface IScrollArrow {
  handleScrollForward: () => void;
  handleScrollBack: () => void;
}

const ScrollArrows: React.FC<IScrollArrow> = ({ handleScrollForward, handleScrollBack }) => {
  return (
    <ScrollArrowContainer>
      <ScrollArrow onClick={handleScrollForward}>
        <PhoenixIcon
          svg={chevron_right}
          size={22}
          color={theme.icon.neutral.inverse}
          hoverColor={theme.icon.neutral.inverse}
          alt="Scroll KPI List Right"
          pointer
        />
      </ScrollArrow>
      <ScrollArrow onClick={handleScrollBack}>
        <PhoenixIcon
          svg={chevron_left}
          size={22}
          color={theme.icon.neutral.inverse}
          hoverColor={theme.icon.neutral.inverse}
          alt="Scroll KPI List Right"
          pointer
        />
      </ScrollArrow>
    </ScrollArrowContainer>
  );
};

const ScrollArrow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  border-radius: 4px 0px 0px 4px;
  background-color: ${theme.fill.brand.primary};

  animation: ${theme.fadeIn} 0.5s ease-in-out forwards;
`;

const ScrollArrowContainer = styled.div`
  position: absolute;
  right: 0;
  top: 10px;
  z-index: 2;

  display: flex;
  flex-direction: column;
  gap: 4px;

  cursor: pointer;
`;
